import React, {useCallback, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {BANNER_HEIGHT, useTopBanner} from 'contexts/TopBannerContext'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {getSearchContext} from 'middleware/analytics/selectors'
import {getAnalyticsPageName} from 'modules/meta/selectors'
import {AnimatePresence, motion} from 'motion/react'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {Page} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {Placement} from '@vio/core/src/deepLinking/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {IconBadge} from '@vio/shared/src/appAcquisition/components/IconBadge'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo/BrandLogo'

import {trackBannerClicked, trackBannerDisplayed} from './trackingEvents'

const Banner = styled.button`
  background-color: ${cssTheme.colors.background.brand.inverse};
  height: 100%;
  color: ${cssTheme.colors.content.brand.primary};
  padding: ${cssTheme.layout.spacing.s300} 0px;
  text-align: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200}px;
  cursor: pointer;
  border: none;
  width: 100%;
  font-family: inherit;
`

const DownloadIconWrapper = styled.div`
  width: 43px;
  min-width: 43px;
  height: 43px;
  display: flex;
  margin-left: auto;
  align-items: center;
  justify-content: center;
  padding-right: ${cssTheme.layout.spacing.s250};
`

const TopBannerWrapper = styled(motion.div)`
  height: ${BANNER_HEIGHT}px;
  overflow: hidden;
  z-index: 100;
  position: relative; /* Needed to enforce z-index */
`

const getPlacement = (pageName: Page): Placement => {
  const pageToPlacementMap = {
    [Page.Home]: Placement.HomePageMobileTopAppBannerInstall,
    [Page.Search]: Placement.SrpResultsMobileTopAppBannerInstall,
    [Page.Accommodation]: Placement.AccommodationPageMobileTopAppBannerInstall
  }

  return (
    pageToPlacementMap[pageName] ?? Placement.HomePageMobileTopAppBannerInstall
  )
}

export const MobileTopAppBanner = () => {
  const {brand} = useBrand()
  const {retention} = useBrandCapabilities()
  const {generateSearchDeepLink} = useGenerateAppDownloadUrl()
  const {isVisible, bannerHeight, dismiss} = useTopBanner()
  const pageName = useSelector(getAnalyticsPageName)
  const dispatch = useDispatch()
  const searchContext = useSelector(getSearchContext)

  const placement = getPlacement(pageName)

  // Track displayed event when banner becomes visible
  useEffect(() => {
    if (isVisible) {
      trackBannerDisplayed(pageName, searchContext, placement, dispatch)
    }
  }, [isVisible])

  const handleBannerClick = useCallback(() => {
    const appDownloadUrl = generateSearchDeepLink(placement)
    trackBannerClicked(pageName, searchContext, placement, dispatch)

    setTimeout(() => {
      window.open(appDownloadUrl, '_blank')
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }, [generateSearchDeepLink, pageName, searchContext, placement, dispatch])

  const handleCloseClick = useCallback(
    (event: React.MouseEvent) => {
      // Prevent the banner click from firing when close button is clicked
      event.stopPropagation()

      // Dismiss the banner
      dismiss()
    },
    [dismiss]
  )

  return (
    <AnimatePresence>
      {isVisible && (
        <TopBannerWrapper
          style={{
            height: BANNER_HEIGHT,
            maxHeight: BANNER_HEIGHT,
            transformOrigin: 'top'
          }}
          initial={{height: BANNER_HEIGHT}} // Start at full height to prevent CLS
          animate={{
            height: bannerHeight
          }}
          exit={{height: 0}}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }}
        >
          <Banner
            onClick={handleBannerClick}
            aria-label="Switch to the Vio app for better deals and exclusive savings"
            type="button"
          >
            <Button
              variant="ghostInverse"
              size="sm"
              onClick={handleCloseClick}
              style={{padding: `0 ${cssTheme.layout.spacing.s200}`}}
            >
              <Icon name="Close" size="xs" />
            </Button>
            {retention.appBannerStyle === 'plain' ? (
              <div>
                <BrandLogo height={48} brand={brand} compact />
              </div>
            ) : (
              <IconBadge
                iconElement={<BrandLogo height={29} brand={brand} compact />}
              />
            )}

            <Box
              paddingTop={cssTheme.layout.spacing.s300}
              paddingBottom={cssTheme.layout.spacing.s300}
              paddingRight={cssTheme.layout.spacing.s200}
              paddingLeft={cssTheme.layout.spacing.s300}
            >
              <Text
                variant="labelXS"
                align="left"
                colorPath="content.neutral.inverse"
              >
                <FormattedMessageWrapper
                  id="switchToTheApp"
                  defaultMessage="Switch to the app"
                />
              </Text>
              <Text
                variant="bodyXS"
                align="left"
                colorPath="content.neutral.inverse"
              >
                {pageName === Page.Home ? (
                  <FormattedMessageWrapper
                    id="accessBestDealsExclusiveSavings"
                    defaultMessage="Access our best deals and exclusive savings"
                  />
                ) : (
                  <FormattedMessageWrapper
                    id="accessBestDealsPriceTrackingRealTime"
                    defaultMessage="Access our best deals, track prices, and get updates in real time."
                  />
                )}
              </Text>
            </Box>
            <DownloadIconWrapper>
              <Icon
                name="DownloadControl"
                size="md"
                colorPath="content.neutral.inverse"
              />
            </DownloadIconWrapper>
          </Banner>
        </TopBannerWrapper>
      )}
    </AnimatePresence>
  )
}
